'use client';

import React from 'react';
import { Box, FormLabel, TextField, Typography } from '@mui/material';
import { formatCurrency } from '@crm-src/lib/utils';

interface TotalsCardProps {
  documentType: 'invoice' | 'quote';
  subTotal: number;
  marketDiscount: number;
  resellerDiscount: number;
  additionalDiscountAmount: number;
  additionalDiscountPercentage: number;
  taxPercentage: number;
  taxAmount: number;
  totalAmount: number;
  balanceDue: number;
  // Canonical discount breakdown from computeInvoiceTotals — the card
  // only DISPLAYS these, it never recomputes (that double-maths is the
  // bug this fix removed).
  marketDiscountAmount: number;
  resellerDiscountAmount: number;
  additionalDiscountValue: number;
  updateMarketDiscount: (value: number) => void;
  updateResellerDiscount: (value: number) => void;
  updateAdditionalDiscountAmount: (value: number) => void;
  updateAdditionalDiscountPercentage: (value: number) => void;
  updateTaxPercentage: (value: number) => void;
}

// Hide the native number-input spinners (was the Tailwind
// `[appearance:textfield] [&::-webkit-*-spin-button]:appearance-none`
// utility chain) so the right-aligned percentage/amount inputs read as
// plain numeric fields.
const noSpinnerSx = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input': { textAlign: 'right' },
} as const;

export const TotalsCard: React.FC<TotalsCardProps> = ({
  documentType,
  subTotal,
  marketDiscount,
  resellerDiscount,
  additionalDiscountAmount,
  additionalDiscountPercentage,
  taxPercentage,
  taxAmount,
  totalAmount,
  balanceDue,
  marketDiscountAmount,
  resellerDiscountAmount,
  additionalDiscountValue,
  updateMarketDiscount,
  updateResellerDiscount,
  updateAdditionalDiscountAmount,
  updateAdditionalDiscountPercentage,
  updateTaxPercentage,
}) => {
  return (
    <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{ width: 480, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box component="span">Sub Total:</Box>
            <Box component="span" sx={{ fontWeight: 500 }}>{formatCurrency(subTotal)}</Box>
          </Box>

          {/* Discounts Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, borderTop: 1, borderColor: "divider", pt: 1 }}>
            {/* Market Discount */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <FormLabel htmlFor="market_discount" sx={{ fontSize: 14 }}>Market Discount (%):</FormLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  id="market_discount"
                  type="number"
                  size="small"
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                  value={marketDiscount || ''}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow empty value
                    if (value === '') {
                      updateMarketDiscount(0);
                      return;
                    }

                    // Parse and validate
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      updateMarketDiscount(numValue);
                    }
                  }}
                  onBlur={(e) => {
                    // On blur, ensure we have a valid number
                    const value = e.target.value;
                    if (value === '' || isNaN(parseFloat(value))) {
                      updateMarketDiscount(0);
                    }
                  }}
                  placeholder="0.0"
                  sx={{ width: 80, ...noSpinnerSx }}
                />
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>%</Typography>
              </Box>
            </Box>
            {marketDiscount > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "text.secondary" }}>
                <Box component="span">Market Discount ({marketDiscount}%):</Box>
                <Box component="span">-{formatCurrency(marketDiscountAmount)}</Box>
              </Box>
            )}

            {/* Reseller Discount */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <FormLabel htmlFor="reseller_discount" sx={{ fontSize: 14 }}>Reseller Discount (%):</FormLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  id="reseller_discount"
                  type="number"
                  size="small"
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                  value={resellerDiscount || ''}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow empty value
                    if (value === '') {
                      updateResellerDiscount(0);
                      return;
                    }

                    // Parse and validate
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      updateResellerDiscount(numValue);
                    }
                  }}
                  onBlur={(e) => {
                    // On blur, ensure we have a valid number
                    const value = e.target.value;
                    if (value === '' || isNaN(parseFloat(value))) {
                      updateResellerDiscount(0);
                    }
                  }}
                  placeholder="0.0"
                  sx={{ width: 80, ...noSpinnerSx }}
                />
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>%</Typography>
              </Box>
            </Box>
            {resellerDiscount > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "text.secondary" }}>
                <Box component="span">Reseller Discount ({resellerDiscount}%):</Box>
                <Box component="span">-{formatCurrency(resellerDiscountAmount)}</Box>
              </Box>
            )}

            {/* Additional Discount */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <FormLabel htmlFor="additional_discount_amount" sx={{ fontSize: 14, whiteSpace: "nowrap" }}>Additional Discount on:</FormLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>$</Typography>
                <TextField
                  id="additional_discount_amount"
                  type="number"
                  size="small"
                  inputProps={{ step: "0.01", min: "0" }}
                  value={additionalDiscountAmount || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateAdditionalDiscountAmount(0);
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0) {
                      updateAdditionalDiscountAmount(numValue);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === '' || isNaN(parseFloat(value))) {
                      updateAdditionalDiscountAmount(0);
                    }
                  }}
                  placeholder="0.00"
                  sx={{ width: 112, ...noSpinnerSx }}
                />
                <TextField
                  id="additional_discount_percentage"
                  type="number"
                  size="small"
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                  value={additionalDiscountPercentage || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateAdditionalDiscountPercentage(0);
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      updateAdditionalDiscountPercentage(numValue);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === '' || isNaN(parseFloat(value))) {
                      updateAdditionalDiscountPercentage(0);
                    }
                  }}
                  placeholder="0.0"
                  sx={{ width: 80, ...noSpinnerSx }}
                />
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>%</Typography>
              </Box>
            </Box>
            {additionalDiscountAmount > 0 && additionalDiscountPercentage > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "text.secondary" }}>
                <Box component="span">Additional Discount ({additionalDiscountPercentage}% of {formatCurrency(additionalDiscountAmount)}):</Box>
                <Box component="span">-{formatCurrency(additionalDiscountValue)}</Box>
              </Box>
            )}
          </Box>

          {/* Tax Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, borderTop: 1, borderColor: "divider", pt: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <FormLabel htmlFor="tax_percentage" sx={{ fontSize: 14 }}>VAT Rate (%):</FormLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  id="tax_percentage"
                  type="number"
                  size="small"
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                  value={taxPercentage || ''}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow empty value
                    if (value === '') {
                      updateTaxPercentage(0);
                      return;
                    }

                    // Parse and validate
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      updateTaxPercentage(numValue);
                    }
                  }}
                  onBlur={(e) => {
                    // On blur, ensure we have a valid number
                    const value = e.target.value;
                    if (value === '' || isNaN(parseFloat(value))) {
                      updateTaxPercentage(0);
                    } else {
                      const numValue = parseFloat(value);
                      updateTaxPercentage(numValue);
                    }
                  }}
                  placeholder="0.0"
                  sx={{ width: 80, ...noSpinnerSx }}
                />
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>%</Typography>
              </Box>
            </Box>
            {taxPercentage > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <Box component="span">VAT Amount ({taxPercentage}%):</Box>
                <Box component="span" sx={{ fontWeight: 500 }}>{formatCurrency(taxAmount)}</Box>
              </Box>
            )}
          </Box>

          {/* A grand total of $0.00 is a legitimate document (CEO, 2026-09-18):
              a fully discounted order, a no-charge warranty replacement, a
              quote made only of "included" catalogue lines. It used to turn
              red after a failed submit and print "Enter quantities and prices
              above" — the visual half of the `total_amount > 0` rule that was
              removed from the validator and from crm-api. Neither the colour
              nor the warning may come back: the form now accepts the document
              the operator is being told to fix. */}
          <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "1.125rem", fontWeight: 600, borderTop: 1, borderColor: "divider", pt: 1 }}>
            <Box component="span">Total:</Box>
            <Box component="span">{formatCurrency(totalAmount)}</Box>
          </Box>
          {/* Balance Due - Only show for invoices, not quotes */}
          {documentType === 'invoice' && (
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "1.125rem", fontWeight: 700, color: "primary.main" }}>
              <Box component="span">Balance Due:</Box>
              <Box component="span">{formatCurrency(balanceDue)}</Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
